import React, {Fragment, PureComponent, ReactNode} from 'react';
import I18n from 'app/i18n';
import PrizeCard from 'app/components/reusable/PrizeCard';
import ActionButton from 'app/components/my_profile/artifacts/action_button';
import {artifactStore} from 'app/store/global';
import {modalAlert} from 'app/utils/modals/popups';
import {translatedName} from 'app/utils/translations_helper';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import InventoryItemInterface from 'app/interfaces/InventoryItemInterface';
import MultiLangsInterface from 'app/interfaces/MultiLangsInterface';
import {type ArtifactLike} from 'app/utils/artifactToLoot';

const WARNING_ICON_SIZE = 32;

interface ArtifactComponent extends ArtifactLike {
    id?: number;
    langs?: MultiLangsInterface;
    slug?: string;
}

interface Props extends Omit<InventoryItemInterface, 'components'> {
    close: () => void;
    components: ArtifactComponent[];
    publicView: () => boolean;
}

interface State {
    disassembling: boolean;
    disassemblingError: string | false;
}

export default class PopupFooter extends PureComponent<Props, State> {
    state: State = {
        disassembling: false,
        disassemblingError: false
    };

    disassemble = (): void => {
        const {publicView, close, ...artifact} = this.props;

        if (publicView()) {
            return;
        }

        artifactStore.disassemble(
            artifact as InventoryItemInterface,
            () => modalAlert(I18n.t('disassemble_success', {name: translatedName(artifact)})),
            close,
            (error: string) => {
                this.setState({disassemblingError: error});
            }
        );
    };

    startDisassemble = (): void => {
        const {publicView} = this.props;

        if (publicView()) {
            return;
        }

        this.setState({
            disassembling: true,
            disassemblingError: false
        });
    };

    cancelDisassembling = (): void => {
        this.setState({
            disassembling: false,
            disassemblingError: false
        });
    };

    renderComponents = (): ReactNode => {
        const {components} = this.props;

        return components.map((component: ArtifactComponent, index: number) => <div
            key={`c${index}`}
            className="inventory__popup__footer--item"
        >
            <PrizeCard
                artifact={component}
                hideMeta
                hideName
            />
            <div className="inventory__popup__footer--item--title">{translatedName(component)}</div>
        </div>);
    };

    render(): ReactNode {
        const {craft_points} = this.props;
        const {disassembling, disassemblingError} = this.state;

        if (!this.props.components || this.props.components.length === 0) {
            return null;
        }

        return <div className="popup__footer">
            <div className="inventory__popup__footer">
                {!disassembling && <Fragment>
                    <p>{I18n.t('disassemble_to')}</p>
                    <div className="inventory__popup__footer--row">
                        <div className="inventory__popup__footer--items">
                            {this.renderComponents()}
                        </div>
                        <div className="inventory__popup__footer--actions">
                            <ActionButton
                                {...this.props}
                                onClickHandler={this.startDisassemble}
                                btnText={I18n.t('disassemble')}
                                btnClass="btn--invert"
                            />
                        </div>
                    </div>
                </Fragment>}
                {disassembling && <div className="inventory__popup__footer--row">
                    {disassemblingError
                        ? <div
                            className="inventory__popup__footer--message inventory__popup__footer--message--error"
                        >
                            <SpriteIcon
                                name="#svg-warning"
                                size={{height: WARNING_ICON_SIZE, width: WARNING_ICON_SIZE}}
                            />
                            <div className="inventory__popup__footer--message--error--content">
                                <div className="inventory__popup__footer--message--error--title">
                                    {I18n.t('error')}
                                </div>
                                <p>{disassemblingError}</p>
                            </div>
                        </div>
                        : <div
                            className="inventory__popup__footer--message"
                            dangerouslySetInnerHTML={{
                                __html: I18n.t('disassemble_confirm',
                                    {points: `<span class="price">${craft_points}</span>`})
                            }}/>}
                    <div className="inventory__popup__footer--actions">
                        <ActionButton
                            {...this.props}
                            onClickHandler={this.disassemble}
                            btnText={I18n.t('confirm_button_yes')}
                        />
                        <ActionButton
                            {...this.props}
                            onClickHandler={this.cancelDisassembling}
                            btnText={I18n.t('confirm_button_no')}
                            btnClass="btn--invert"
                        />
                    </div>
                </div>}
            </div>
        </div>;
    }
}
